import Newsletter from "./Newsletter"
import IconButton from "@mui/material/IconButton"
import { styled } from "@mui/material/styles"
import Icon from "Components/Icon/Icon"
import { getNewsletter } from "data/contentData/api/newsletter"
import { INewsletterCard } from "data/contentData/interfaces/mongodb/INewsletter"
import { setNewsletterCookie } from "data/internal/api/newsletterCookie"
import { getCookie } from "lib/getCookie"
import { useEffect, useRef, useState } from "react"

export default function NewsletterPopover({ path }: { path: string }) {
  const [newsletterData, setNewsletterData] = useState<
    INewsletterCard | undefined
  >(undefined)
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)

  // fetch newsletter data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const data = await getNewsletter(path)
        if (data) setNewsletterData(data)
      } catch (error) {
        console.error("Error fetching newsletter data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [path])

  const [triggerPoint, setTriggerPoint] = useState(9999)

  useEffect(() => {
    const height = document.documentElement.scrollHeight
    setTriggerPoint(height * 0.3)
  }, [])

  const sentinelRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setOpen(true)
          observer.disconnect()
        }
      },
      { threshold: 0 }
    )

    if (sentinelRef.current) observer.observe(sentinelRef.current)

    return () => observer.disconnect()
  }, [])

  // do not show popover if cookie is set or if showAsPopup is false
  const shouldHide =
    !newsletterData ||
    newsletterData.showAsPopup === false ||
    getCookie(newsletterData.formId) === newsletterData.newsletterId

  const handleClose = () => {
    setNewsletterCookie(newsletterData!.formId, newsletterData!.newsletterId)
    setOpen(false)
  }

  return (
    <>
      <div
        id="test123"
        ref={sentinelRef}
        style={{
          position: "absolute",
          top: `${triggerPoint}px`, // Use pixels based on document height
          height: "1px",
          width: "1px",
        }}
      />

      {newsletterData && open && !shouldHide && !loading && (
        <StyledContainer>
          <StyledCloseButton
            color="primary"
            aria-label="Lukk"
            onClick={handleClose}
          >
            <Icon name="close" />
          </StyledCloseButton>

          <Newsletter
            placementId="newsletter-popover"
            popupBackground={newsletterData.popupBackground}
            data={newsletterData}
          />
        </StyledContainer>
      )}
    </>
  )
}

const StyledContainer = styled("div")(({ theme }) => ({
  boxShadow: theme.shadows[3],
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.newsletter.main,
  padding: 0,
  position: "sticky",
  bottom: 0,
  overflow: "hidden",
  [theme.breakpoints.up("md")]: {
    bottom: "50%",
    margin: theme.spacing(0, 4),
  },
}))

const StyledCloseButton = styled(IconButton)({
  position: "absolute",
  alignSelf: "flex-end",
})
